// Curated multi-day itineraries across the Himalayas

export const BRAND_TIERS = {
  adventure: { label: 'Adventure Partners', brands: ['Royal Enfield', 'Mahindra Adventure'] },
  luxury: { label: 'Luxury Mobility Partners', brands: ['Volkswagen', 'Mercedes-Benz'] },
  outdoor: { label: 'Gear & Outdoor', brands: ['Decathlon', 'The North Face', 'Wildcraft'] },
};

export const ITINERARIES = [
  {
    slug: 'spiti-circuit',
    title: 'The Spiti Circuit',
    subtitle: 'Manali to Kaza and back through the moon',
    region: 'Himachal Pradesh',
    duration: 7,
    difficulty: 'Challenging',
    vibe: ['adventure', 'motorcycle', 'high-altitude'],
    maxAltitude: '4,551m (Kunzum La)',
    bestSeason: 'June — September',
    groupSize: '8-12',
    priceRange: '₹35,000 — ₹45,000',
    pricePer: 'per person, all-inclusive',
    heroImage: 'https://images.unsplash.com/photo-1670644654521-6fbfffeaba87?w=1200&q=80',
    cardImage: 'https://images.unsplash.com/photo-1670644654521-6fbfffeaba87?w=600&q=80',
    description: 'Seven days through one of the world\'s most dramatic landscapes — where the road itself is the destination. From the lush Kullu Valley through the barren grandeur of Lahaul, over Kunzum La into the high-desert kingdom of Spiti. Ancient monasteries, fossil-rich valleys, the highest post office, and nights under skies so clear they feel invented.',
    partnerOpportunity: 'adventure',
    departures: [
      { date: 'June 14-20, 2026', status: 'open', spots: 12 },
      { date: 'July 19-25, 2026', status: 'open', spots: 12 },
      { date: 'August 23-29, 2026', status: 'open', spots: 12 },
      { date: 'September 13-19, 2026', status: 'open', spots: 12 },
    ],
    days: [
      {
        day: 1,
        title: 'Manali Acclimatization',
        location: 'Manali, Kullu Valley',
        altitude: '2,050m',
        drive: null,
        description: 'Arrive in Manali. Settle into Old Manali — walk the Manalsu river trail, eat at a riverside cafe, prep for the road ahead. Evening briefing and bike/vehicle check.',
        stay: 'Curated homestay in Old Manali',
        highlights: ['Manalsu river walk', 'Old Manali cafes', 'Vehicle briefing'],
      },
      {
        day: 2,
        title: 'Manali to Sissu via Atal Tunnel',
        location: 'Sissu, Lahaul',
        altitude: '3,100m',
        drive: '85 km · ~3 hours',
        description: 'Through the 9.2km Atal Tunnel into Lahaul — the landscape shifts from pine forests to stark, glacial valleys in minutes. Stop at Sissu waterfall. The air thins. The sky opens.',
        stay: 'Homestay in Sissu',
        highlights: ['Atal Tunnel crossing', 'Sissu Waterfall', 'First Lahaul views'],
      },
      {
        day: 3,
        title: 'Sissu to Kaza via Kunzum La',
        location: 'Kaza, Spiti Valley',
        altitude: '3,650m',
        drive: '130 km · ~6 hours',
        description: 'The big crossing. Over Kunzum La (4,551m) — prayer flags, thin air, and the entire Spiti Valley laid out below like a topographic map. Descend into Kaza, the administrative heart of Spiti.',
        stay: 'Homestay in Kaza',
        highlights: ['Kunzum La pass (4,551m)', 'First Spiti Valley views', 'Kaza market walk'],
      },
      {
        day: 4,
        title: 'Kaza — Key & Kibber',
        location: 'Kaza / Kibber',
        altitude: '4,205m (Kibber)',
        drive: '40 km · ~2 hours (round trip)',
        description: 'Morning at Key Monastery — 1,000 years old, perched impossibly on a hilltop. Then up to Kibber, one of the highest inhabited villages in the world. Look for ibex on the cliffs. Return to Kaza for sunset.',
        stay: 'Homestay in Kaza',
        highlights: ['Key Monastery', 'Kibber village', 'Ibex spotting', 'Highest post office'],
      },
      {
        day: 5,
        title: 'Kaza to Tabo',
        location: 'Tabo',
        altitude: '3,280m',
        drive: '50 km · ~2 hours',
        description: 'Drive along the Spiti river to Tabo — home to the "Ajanta of the Himalayas." The 1,000-year-old Tabo Monastery has murals that rival anything in Rajasthan. Walk the chhortens at sunset. Sleep in silence.',
        stay: 'Homestay in Tabo',
        highlights: ['Tabo Monastery murals', 'Dhankar Gompa viewpoint', 'Spiti river trail'],
      },
      {
        day: 6,
        title: 'Tabo to Nako',
        location: 'Nako',
        altitude: '3,660m',
        drive: '110 km · ~5 hours',
        description: 'Through the dramatic Hangrang Valley towards Kinnaur. Stop at Nako Lake — a sacred turquoise jewel surrounded by apricot orchards and prayer wheels. The last night in the high desert.',
        stay: 'Homestay in Nako',
        highlights: ['Hangrang Valley', 'Nako Lake', 'Apricot orchards', 'Star gazing'],
      },
      {
        day: 7,
        title: 'Nako to Manali (or Shimla)',
        location: 'Return',
        altitude: 'Descending',
        drive: '250 km · ~10 hours (or split)',
        description: 'The long descent. Through Kalpa, Recong Peo, and the Kinnaur Valley — apple orchards, the Sutlej gorge, and the Hindustan-Tibet Highway. The world gets greener with every kilometre. Arrive back changed.',
        stay: null,
        highlights: ['Kinnaur Valley', 'Apple orchards', 'Sutlej Gorge', 'Hindustan-Tibet Highway'],
      },
    ],
    included: ['All accommodation (homestays)', 'Breakfast & dinner daily', 'Local guide', 'Permits & entry fees', 'Emergency support vehicle'],
    notIncluded: ['Travel to/from Manali', 'Motorcycle/vehicle rental', 'Lunch', 'Personal expenses', 'Travel insurance'],
  },
  {
    slug: 'tirthan-jibhi-sainj',
    title: 'Tirthan, Jibhi & Sainj',
    subtitle: 'The slow valley trail through Himachal\'s quietest corners',
    region: 'Himachal Pradesh',
    duration: 4,
    difficulty: 'Easy',
    vibe: ['slow-travel', 'nature', 'riverside'],
    maxAltitude: '2,400m (Jalori Pass)',
    bestSeason: 'March — November',
    groupSize: '6-10',
    priceRange: '₹18,000 — ₹25,000',
    pricePer: 'per person, all-inclusive',
    heroImage: 'https://images.unsplash.com/photo-1701517857774-ce9c3c07bd39?w=1200&q=80',
    cardImage: 'https://images.pexels.com/photos/2961109/pexels-photo-2961109.jpeg?auto=compress&cs=tinysrgb&w=600',
    description: 'Four days in the three valleys that most tourists miss — even as they drive through to Manali. Tirthan, Jibhi, and Sainj are where Himachal still feels like Himachal. River crossings, trout streams, forest walks, and homestays run by families who\'ve lived here for generations. No WiFi, no FOMO, no agenda.',
    partnerOpportunity: null,
    departures: [
      { date: 'May 9-12, 2026', status: 'open', spots: 10 },
      { date: 'June 6-9, 2026', status: 'open', spots: 10 },
      { date: 'September 19-22, 2026', status: 'open', spots: 10 },
      { date: 'October 17-20, 2026', status: 'open', spots: 10 },
    ],
    days: [
      {
        day: 1,
        title: 'Arrive Tirthan Valley',
        location: 'Tirthan Valley, Banjar',
        altitude: '1,600m',
        drive: 'From Aut (NH21) · 30 km · 1 hour',
        description: 'Turn off the highway at Aut and drop into a different Himachal. The Tirthan river appears — cold, clear, full of trout. Check into a riverside homestay. Afternoon walk along the river. Evening bonfire with the host family.',
        stay: 'Brown Dipper House or partner homestay',
        stayLink: '/stay/brown-dipper-house',
        highlights: ['Tirthan river walk', 'Trout spotting', 'Bonfire with hosts'],
      },
      {
        day: 2,
        title: 'Tirthan — Great Himalayan National Park',
        location: 'GHNP Buffer Zone',
        altitude: '1,600 — 2,200m',
        drive: null,
        description: 'Guided nature walk into the buffer zone of the UNESCO-listed Great Himalayan National Park. Birdwatching (the valley has 200+ species), waterfall trails, packed lunch by the river. Return for a home-cooked pahadi dinner.',
        stay: 'Same homestay',
        highlights: ['GHNP buffer zone trek', 'Birdwatching (200+ species)', 'Waterfall trail', 'Pahadi dinner'],
      },
      {
        day: 3,
        title: 'Tirthan to Jibhi via Jalori Pass',
        location: 'Jibhi',
        altitude: '2,400m (Jalori) → 1,800m (Jibhi)',
        drive: '35 km · ~2 hours',
        description: 'Drive up to Jalori Pass — at 2,400m, it\'s one of the lowest motorable passes in Himachal, but the views are outsized. Short hike to Serolsar Lake through dense oak and rhododendron forest. Descend to Jibhi — quieter, darker, deeper in the valley.',
        stay: 'Curated homestay in Jibhi',
        highlights: ['Jalori Pass crossing', 'Serolsar Lake hike', 'Oak & rhododendron forest'],
      },
      {
        day: 4,
        title: 'Jibhi — Sainj Valley — Departure',
        location: 'Sainj Valley',
        altitude: '1,400m',
        drive: '25 km · ~1 hour to Sainj',
        description: 'Morning walk through Jibhi\'s wooden temples and slate-roofed villages. Drive to Sainj Valley — even quieter, even greener. Riverside lunch. The valleys get harder to leave the further in you go. Depart from Aut or continue to Manali.',
        stay: null,
        highlights: ['Jibhi wooden temples', 'Sainj Valley', 'Riverside lunch', 'Slate-roof villages'],
      },
    ],
    included: ['All accommodation (curated homestays)', 'Breakfast & dinner daily', 'GHNP guide & permits', 'Local transfers between valleys', 'Jalori Pass transport'],
    notIncluded: ['Travel to/from Aut/Manali', 'Lunch (Day 1 & 4)', 'Personal expenses', 'Travel insurance'],
  },
  {
    slug: 'tirthan-jibhi-circuit',
    title: 'Tirthan & Jibhi Circuit',
    subtitle: 'Your first Himachal, done the slow way',
    region: 'Himachal Pradesh',
    duration: 6,
    difficulty: 'Easy',
    vibe: ['slow-travel', 'first-timers', 'riverside', 'forest'],
    maxAltitude: '3,100m (Serolsar Lake)',
    bestSeason: 'March — June · September — November',
    groupSize: '4-10',
    priceRange: '₹22,000 — ₹30,000',
    pricePer: 'per person, all-inclusive',
    heroImage: 'https://images.unsplash.com/photo-1593181629936-11c609b8db9b?w=1200&q=80',
    cardImage: 'https://images.unsplash.com/photo-1593181629936-11c609b8db9b?w=600&q=80',
    description: 'Five nights, six days in the two valleys that convert travellers into regulars. Tirthan for the river, Sojha-Jibhi for the forest. Curated stays by the water, a drive over Jalori Pass, a hike to a sacred alpine lake, and just enough café culture to feel like a long weekend and a reset at the same time. Designed for first-time Himachal travellers who want substance without the standard Manali-Kasol cliché.',
    partnerOpportunity: null,
    departures: [
      { date: 'April 18-23, 2026', status: 'open', spots: 10 },
      { date: 'May 16-21, 2026', status: 'open', spots: 10 },
      { date: 'September 26 — October 1, 2026', status: 'open', spots: 10 },
      { date: 'October 24-29, 2026', status: 'open', spots: 10 },
    ],
    days: [
      {
        day: 1,
        title: 'Arrive Tirthan Valley',
        location: 'Tirthan Valley, Gushaini',
        altitude: '1,600m',
        drive: 'From Aut (NH21) · 30 km · 1 hour',
        description: 'Turn off the highway at Aut and drop into a different Himachal. The Tirthan river appears — cold, clear, alive with trout. Check into Bluehouse by the River, settle in slowly. Afternoon walk along the Tirthan, bird-spotting, and an evening bonfire with the hosts.',
        stay: 'Bluehouse by the River',
        stayLink: '/collective/bluehouse',
        highlights: ['Tirthan riverside arrival', 'Bluehouse bonfire', 'Slow unwinding'],
      },
      {
        day: 2,
        title: 'Tirthan — Trout, Trails & GHNP Buffer',
        location: 'GHNP Buffer Zone',
        altitude: '1,600 — 2,200m',
        drive: null,
        description: 'Morning at the trout farm — watch the hatchery, try your hand at fly-fishing (catch-and-release). Afternoon nature walk into the buffer zone of the UNESCO-listed Great Himalayan National Park with a local guide. Evening: riverside reading, card games, and a home-cooked pahadi thali.',
        stay: 'Bluehouse by the River',
        stayLink: '/collective/bluehouse',
        highlights: ['Trout farm visit', 'GHNP buffer walk', 'Riverside dinner', 'Pahadi thali'],
      },
      {
        day: 3,
        title: 'Tirthan to Sojha via Jalori Pass',
        location: 'Sojha (near Jibhi)',
        altitude: '2,400m (Jalori) → 2,680m (Sojha)',
        drive: '40 km · ~2 hours',
        description: 'The Circuit\'s headline drive. Up through deodar and oak forests to Jalori Pass — one of the lowest motorable passes in Himachal, with views that punch way above their altitude. Tea at the pass. A short drive down the other side to Sojha — a quiet Seraj Valley village tucked in conifer forest, about 5km from the pass. Check into our mountain lodge in Sojha — two wooden houses with en-suite rooms and a kitchen that turns out exceptional pahadi cooking.',
        stay: 'Sojha, Tirthan Valley',
        stayLink: '/stay/sojha',
        highlights: ['Jalori Pass drive', 'Oak & deodar forests', 'Tea at the top', 'Arrival at Sojha lodge'],
      },
      {
        day: 4,
        title: 'Serolsar Lake Hike',
        location: 'Serolsar Lake',
        altitude: '3,100m',
        drive: '10 km · 25 min drive to trailhead',
        description: 'The spiritual heart of the circuit. Drive back up to Jalori Pass, then hike 5km through old-growth oak, rhododendron, and maple to Serolsar — a small, sacred alpine lake ringed by forest, believed to be cared for by the Jalpa Devi. Picnic lunch at the lake. Return to Sojha by evening for a hot pahadi dinner at the lodge.',
        stay: 'Sojha, Tirthan Valley',
        stayLink: '/stay/sojha',
        highlights: ['5km forest trek', 'Serolsar Lake', 'Jalpa Devi temple', 'Pahadi dinner at lodge'],
      },
      {
        day: 5,
        title: 'Jibhi Day — Café Culture & Waterfall Walk',
        location: 'Jibhi & Ghiyagi',
        altitude: '1,800 — 2,000m',
        drive: '12 km · 30 min drive to Jibhi',
        description: 'A day designed to go slow. Drive down from Sojha to Jibhi village — 12km of descending forest road. Morning walk to the Jibhi Waterfall through a gorge carpeted in moss. Afternoon café-hop through Jibhi — the valley has quietly become one of Himachal\'s best coffee towns, with a handful of small, owner-run cafés roasting beans sourced from across the Himalayas. Browse artisan shops. Sunset drive back up to Sojha. Final dinner at the lodge.',
        stay: 'Sojha, Tirthan Valley',
        stayLink: '/stay/sojha',
        highlights: ['Jibhi Waterfall walk', 'Café-hopping in Jibhi', 'Artisan shops', 'Sunset drive to Sojha'],
      },
      {
        day: 6,
        title: 'Sojha to Aut — Departure',
        location: 'Aut (NH21)',
        altitude: '950m',
        drive: '45 km · ~1.75 hours',
        description: 'A slow morning. Final breakfast at the lodge. Drive back down through Jibhi and the Banjar valley to Aut — where the Volvo buses stop and the highway to Delhi begins. Depart, already planning the return.',
        stay: null,
        highlights: ['Final lodge breakfast', 'Descent to Aut', 'Volvo / onward travel'],
      },
    ],
    included: ['All accommodation (Bluehouse + Sojha Lodge)', 'Breakfast & dinner daily', 'GHNP buffer guide', 'Jalori Pass & Serolsar transport', 'Local transfers'],
    notIncluded: ['Travel to/from Aut (Volvo or self-drive)', 'Lunch (except Serolsar day picnic)', 'Fly-fishing gear rental', 'Personal expenses', 'Travel insurance'],
  },
  {
    slug: 'meghalaya-living-roots',
    title: 'Meghalaya Living Roots',
    subtitle: 'Through the Abode of Clouds to the cleanest village in Asia',
    region: 'Meghalaya',
    duration: 5,
    difficulty: 'Moderate',
    vibe: ['northeast', 'culture', 'trekking', 'waterfalls'],
    maxAltitude: '1,965m (Shillong Peak)',
    bestSeason: 'October — April',
    groupSize: '8-12',
    priceRange: '₹28,000 — ₹38,000',
    pricePer: 'per person, all-inclusive',
    heroImage: 'https://images.pexels.com/photos/34017584/pexels-photo-34017584.jpeg?auto=compress&cs=tinysrgb&w=1200',
    cardImage: 'https://images.pexels.com/photos/27820314/pexels-photo-27820314.jpeg?auto=compress&cs=tinysrgb&w=600',
    description: 'Five days in India\'s wettest, greenest, most surreal state. Living root bridges grown over centuries by Khasi tribes. Waterfalls that drop into Bangladesh. The cleanest village in Asia. Caves, canyons, and a cuisine built on fermented bamboo and smoked pork. Meghalaya is India\'s best-kept secret — and it won\'t stay that way.',
    partnerOpportunity: 'outdoor',
    departures: [
      { date: 'October 10-14, 2026', status: 'open', spots: 12 },
      { date: 'November 14-18, 2026', status: 'open', spots: 12 },
      { date: 'December 19-23, 2026', status: 'open', spots: 12 },
      { date: 'February 13-17, 2027', status: 'open', spots: 12 },
    ],
    days: [
      {
        day: 1,
        title: 'Arrive Shillong',
        location: 'Shillong',
        altitude: '1,496m',
        drive: 'From Guwahati · 100 km · 3 hours',
        description: 'Drive from Guwahati through rolling Khasi Hills. Shillong feels like a hill station that refused to grow old — live music cafes, Police Bazar bustle, and Iewduh (the largest market in the Northeast). Evening walk to Ward\'s Lake.',
        stay: 'Heritage homestay in Shillong',
        highlights: ['Iewduh market', 'Ward\'s Lake', 'Live music cafes', 'Khasi cuisine intro'],
      },
      {
        day: 2,
        title: 'Shillong to Cherrapunji',
        location: 'Cherrapunji (Sohra)',
        altitude: '1,484m',
        drive: '55 km · ~2 hours',
        description: 'To the wettest place on earth. Nohkalikai Falls — India\'s tallest plunge waterfall (340m). The Seven Sisters Falls. Mawsmai Cave. The landscape is vertical, green, and constantly dripping. The Khasi people here have built a civilization around rain.',
        stay: 'Homestay in Cherrapunji',
        highlights: ['Nohkalikai Falls (340m)', 'Seven Sisters Falls', 'Mawsmai Cave', 'Eco Park viewpoint'],
      },
      {
        day: 3,
        title: 'Double Decker Living Root Bridge Trek',
        location: 'Nongriat Village',
        altitude: '1,484m → 800m → 1,484m',
        drive: null,
        description: 'The signature trek. 3,500 steps down to Nongriat village and the legendary Double Decker Living Root Bridge — grown over 200 years by training the aerial roots of rubber fig trees across rivers. Swim in the natural rock pools. Climb back up (the real challenge).',
        stay: 'Same homestay in Cherrapunji',
        highlights: ['Double Decker Root Bridge', '3,500-step trek', 'Natural rock pools', 'Nongriat village'],
      },
      {
        day: 4,
        title: 'Cherrapunji to Dawki & Mawlynnong',
        location: 'Dawki / Mawlynnong',
        altitude: '400m (Dawki)',
        drive: '95 km · ~3.5 hours',
        description: 'Drive to Dawki — where the Umngot river is so clear that boats appear to float on glass. Cross to the Bangladesh border viewpoint. Then to Mawlynnong, officially Asia\'s cleanest village, with its bamboo dustbins and living root bridge.',
        stay: 'Community homestay in Mawlynnong',
        highlights: ['Dawki crystal river', 'Umngot boat ride', 'Bangladesh border', 'Mawlynnong — cleanest village in Asia'],
      },
      {
        day: 5,
        title: 'Mawlynnong to Shillong — Departure',
        location: 'Shillong',
        altitude: '1,496m',
        drive: '80 km · ~3 hours',
        description: 'Morning walk through Mawlynnong\'s immaculate bamboo-lined paths. Visit the Sky Walk viewpoint overlooking Bangladesh plains. Drive back to Shillong via Laitlum Canyons — "the end of the hills." Depart from Shillong or Guwahati.',
        stay: null,
        highlights: ['Mawlynnong Sky Walk', 'Laitlum Canyons', 'Khasi Hills drive'],
      },
    ],
    included: ['All accommodation (homestays)', 'Breakfast & dinner daily', 'Root Bridge trek guide', 'All local transfers', 'Dawki boat ride', 'Entry fees'],
    notIncluded: ['Flights to/from Guwahati', 'Lunch', 'Personal expenses', 'Travel insurance', 'Porters (optional)'],
  },
  {
    slug: 'kumaon-heritage-drive',
    title: 'The Kumaon Heritage Drive',
    subtitle: 'Oak forests, colonial bungalows, and the Himalayan wall',
    region: 'Uttarakhand',
    duration: 5,
    difficulty: 'Easy',
    vibe: ['luxury', 'heritage', 'driving', 'slow-travel'],
    maxAltitude: '2,420m (Binsar)',
    bestSeason: 'March — June, September — November',
    groupSize: '4-8',
    priceRange: '₹40,000 — ₹55,000',
    pricePer: 'per person, all-inclusive',
    heroImage: 'https://images.unsplash.com/photo-1632051696912-0fde7545d7c7?w=1200&q=80',
    cardImage: 'https://images.pexels.com/photos/12795418/pexels-photo-12795418.jpeg?auto=compress&cs=tinysrgb&w=600',
    description: 'Five days on the quiet roads of Kumaon — where British-era hill stations have aged into something more interesting. Oak forests so dense they block the sun. Colonial bungalows converted into literary retreats. And behind it all, the 300km wall of the Greater Himalaya, from Nanda Devi to Panchachuli. This is Uttarakhand for people who\'ve done Mussoorie.',
    partnerOpportunity: 'luxury',
    departures: [
      { date: 'April 18-22, 2026', status: 'open', spots: 8 },
      { date: 'May 16-20, 2026', status: 'open', spots: 8 },
      { date: 'October 3-7, 2026', status: 'open', spots: 8 },
      { date: 'November 7-11, 2026', status: 'open', spots: 8 },
    ],
    days: [
      {
        day: 1,
        title: 'Arrive Almora',
        location: 'Almora',
        altitude: '1,638m',
        drive: 'From Kathgodam · 90 km · 3.5 hours',
        description: 'Drive up from Kathgodam through pine forests to Almora — the cultural capital of Kumaon. Walk the cobbled Lala Bazar. Visit the 1,000-year-old Nanda Devi temple. The bazaar smells of bal mithai and singori. Stay in a heritage property overlooking the valley.',
        stay: 'Heritage homestay in Almora',
        highlights: ['Lala Bazar walk', 'Nanda Devi Temple', 'Bal mithai tasting', 'Sunset over Kumaon hills'],
      },
      {
        day: 2,
        title: 'Almora to Binsar',
        location: 'Binsar Wildlife Sanctuary',
        altitude: '2,420m',
        drive: '30 km · ~1.5 hours',
        description: 'Into the oak and rhododendron forests of Binsar Wildlife Sanctuary. The 300km Himalayan panorama from the Zero Point viewpoint — Nanda Devi, Kedarnath, Shivling, Trishul, Panchachuli — is the single greatest mountain view in Uttarakhand. Birdwatching walks. Colonial-era forest bungalow.',
        stay: 'Forest bungalow in Binsar',
        highlights: ['Zero Point 300km Himalayan panorama', 'Oak forest walks', 'Birdwatching (200+ species)', 'Colonial forest bungalow'],
      },
      {
        day: 3,
        title: 'Binsar to Mukteshwar',
        location: 'Mukteshwar',
        altitude: '2,286m',
        drive: '75 km · ~3 hours',
        description: 'Through quiet Kumaon backroads to Mukteshwar — named after the 350-year-old Shiva temple on the cliff edge. The chauri orchards are dense, the views are absurd, and the colonial veterinary institute (now IVRI) campus is a time capsule. Walk to the cliff edge at sunset.',
        stay: 'Boutique homestay in Mukteshwar',
        highlights: ['Mukteshwar Temple cliff edge', 'Chauri orchards', 'IVRI heritage campus', 'Cliff sunset'],
      },
      {
        day: 4,
        title: 'Mukteshwar — Peora — Ramgarh',
        location: 'Ramgarh / Peora',
        altitude: '1,789m',
        drive: '45 km · ~2 hours',
        description: 'The literary belt of Kumaon. Ramgarh is where Tagore wrote, Narain Singh Thapa governed, and fruit orchards carpet every hillside. Visit Peora — the quiet ridge where a new generation of homestays is emerging. Afternoon tea at an orchard estate. Poetry in the air.',
        stay: 'Orchard homestay in Ramgarh',
        highlights: ['Tagore\'s writing retreat', 'Fruit orchard walks', 'Peora ridge views', 'Tea at estate bungalow'],
      },
      {
        day: 5,
        title: 'Ramgarh — Bhimtal — Departure',
        location: 'Bhimtal / Kathgodam',
        altitude: '1,370m (Bhimtal)',
        drive: '35 km · ~1.5 hours to Kathgodam',
        description: 'Gentle drive down to Bhimtal — the quieter, greener cousin of Nainital. Walk around the lake, visit the island aquarium. Continue to Kathgodam for the train back, or drive to Delhi. The Kumaon hills recede in the mirror, but the oak-scented air stays.',
        stay: null,
        highlights: ['Bhimtal Lake', 'Island aquarium', 'Final Kumaon views'],
      },
    ],
    included: ['All accommodation (heritage/boutique homestays)', 'Breakfast & dinner daily', 'Binsar sanctuary permits & guide', 'All local transfers in premium vehicle', 'Orchard estate visit'],
    notIncluded: ['Travel to/from Kathgodam', 'Lunch', 'Personal expenses', 'Travel insurance'],
  },
];

export const VIBE_LABELS = {
  'adventure': 'Adventure',
  'motorcycle': 'Motorcycle',
  'high-altitude': 'High Altitude',
  'slow-travel': 'Slow Travel',
  'nature': 'Nature',
  'riverside': 'Riverside',
  'northeast': 'Northeast',
  'culture': 'Culture',
  'trekking': 'Trekking',
  'waterfalls': 'Waterfalls',
  'luxury': 'Luxury',
  'heritage': 'Heritage',
  'driving': 'Driving',
};

export const DIFFICULTY_COLORS = {
  'Easy': 'bg-green-100 text-green-800',
  'Moderate': 'bg-amber-100 text-amber-800',
  'Challenging': 'bg-red-100 text-red-800',
};
